import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import CustomBreadCrumbs from "../../common/breadcrumbs";
import List from "../../common/list";
import * as ListCellRenders from "../../common/list/ListCellRenders";
import TextInput from "../../common/fields/TextInput";
import { useListSearch } from "../../common/hooks";
// Imported from the leaf modules, not the constants barrel: the barrel
// loads this screen through MenuConstants, and REPORT_ROUTE_BY_KIND reads
// both at module scope (see constants/ImportOrder.test.js).
import * as RouteConstants from "../../../constants/RouteConstants";
import * as SavedReportConstants from "../../../constants/SavedReportConstants";
import { fromReportLevel } from "../../../helpers/SavedReportFilters";
import {
  describeLastSend,
  describeSchedule,
  describeScheduleState,
  formatDateTime,
  sendReason,
} from "../../../helpers/ScheduleSummary";
import {
  savedReportDeleteRequest,
  savedReportHistoryRequest,
  savedReportListRequest,
  savedReportScheduleDeleteRequest,
  savedReportSchedulePauseRequest,
  savedReportScheduleResumeRequest,
  savedReportScheduleSaveRequest,
  savedReportSendNowRequest,
  savedReportSetSearch,
  savedReportUpdateRequest,
} from "../../../store/slices/savedReportSlice";
import SaveReportDialog from "./SaveReportDialog";
import ScheduleDialog from "./ScheduleDialog";
import SendHistoryDialog from "./SendHistoryDialog";

const REPORT_ROUTE_BY_KIND = {
  [SavedReportConstants.SAVED_REPORT_KIND_ADMIN]:
    RouteConstants.APP_ROUTE_REPORTS_LIST,
  [SavedReportConstants.SAVED_REPORT_KIND_MY_TEAM]:
    RouteConstants.APP_ROUTE_MY_TEAM_REPORTS_LIST,
};

// The list's search: the report name contains it, in any case, trimmed (as
// the API trims namePattern before matching, ListSearch.cs).
const matchesName = (report, namePattern) => {
  const term = (namePattern || "").trim().toLowerCase();
  return !term || (report.name || "").toLowerCase().includes(term);
};

// One empty list for every render, so the rows below stay the same until
// what they are built from changes.
const NO_ROWS = [];

export const SavedReportList = ({
  labels,
  additionalProps = {},
  savedReports = NO_ROWS,
  requesting = false,
  history = [],
  sendingNow = NO_ROWS,
  searches = {},
  loadSavedReports,
  setSearch,
  updateSavedReport,
  deleteSavedReport,
  saveSchedule,
  pauseSchedule,
  resumeSchedule,
  deleteSchedule,
  sendNow,
  loadHistory,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const kind =
    additionalProps.kind || SavedReportConstants.SAVED_REPORT_KIND_ADMIN;
  // { type: "rename" | "schedule" | "history", report }
  const [dialog, setDialog] = useState(null);
  const closeDialog = () => setDialog(null);

  // The search this list is filtered by, kept in the persisted store for
  // each kind (Reports and My team), and the box that changes it once typing
  // settles (useListSearch: nothing is sent when the list opens).
  const namePattern = searches?.[kind] || "";
  const [searchText, setSearchText] = useListSearch(namePattern, (value) =>
    setSearch({ kind, namePattern: value })
  );

  useEffect(() => {
    loadSavedReports({ namePattern });
  }, [namePattern, loadSavedReports]);

  // The API filters by namePattern once it supports it (until then it sends
  // every saved report); the rows are filtered here as well, which also keeps
  // a reload without the search (after a schedule change) to the searched
  // rows. The table redraws a row's menu only when the row's data changes
  // (PrimeReact memoizes body cells), so what the menu depends on is put on
  // the row: schedulePaused picks Pause or Resume, sendPending greys Send now.
  // The rows are built again only when what they come from changes: a key
  // typed in the search box renders this screen before the search is kept,
  // and new row objects made the table compare every cell again (code review
  // phase 2, finding 11).
  const reports = useMemo(
    () =>
      savedReports
        .filter((report) => report.kind === kind)
        .filter((report) => matchesName(report, namePattern))
        .map((report) => ({
          ...report,
          schedulePaused: Boolean(report.schedule?.isPaused),
          sendPending: sendingNow.includes(report.id),
        })),
    [savedReports, kind, namePattern, sendingNow]
  );

  const openReport = (report) =>
    navigate(
      `${REPORT_ROUTE_BY_KIND[kind]}?${SavedReportConstants.SAVED_REPORT_QUERY_PARAM}=${report.id}`
    );

  const confirmDelete = (report) =>
    confirmDialog({
      message: intl.formatMessage(
        { id: "saved_report_delete_message" },
        { name: report.name }
      ),
      header: intl.formatMessage({ id: "delete_confirmation" }),
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      rejectClassName: "p-button-secondary",
      accept: () => deleteSavedReport({ id: report.id }),
      reject: () => {},
    });

  // The row menu (common/moreActions). Saved reports belong to their owner,
  // who may use every action; an action that cannot be used yet is greyed
  // with the reason.
  const rowActions = [
    {
      label: "saved_report_open",
      icon: "pi-external-link",
      isAllowed: true,
      action: openReport,
    },
    {
      label: "saved_report_rename",
      icon: "pi-pencil",
      isAllowed: true,
      action: (report) => setDialog({ type: "rename", report }),
    },
    {
      label: "saved_report_schedule",
      icon: "pi-calendar",
      isAllowed: true,
      action: (report) => setDialog({ type: "schedule", report }),
    },
    {
      label: "saved_report_pause",
      icon: "pi-pause",
      isAllowed: true,
      additionalCheckForAllowed: (report) => Boolean(report.schedule),
      additionalCheckNotAllowedMessage: "saved_report_pause_needs_schedule",
      hidden: { field: "schedulePaused", value: true },
      action: (report) => pauseSchedule({ id: report.id }),
    },
    {
      label: "saved_report_resume",
      icon: "pi-play",
      isAllowed: true,
      hidden: { field: "schedulePaused", value: false },
      action: (report) => resumeSchedule({ id: report.id }),
    },
    {
      // The file format lives on the schedule: the API refuses Send now
      // without one.
      label: "saved_report_send_now",
      icon: "pi-send",
      isAllowed: true,
      additionalCheckForAllowed: (report) =>
        Boolean(report.schedule) && !report.sendPending,
      additionalCheckNotAllowedMessage: (report) =>
        report.schedule
          ? "saved_report_send_now_in_progress"
          : "saved_report_send_now_needs_schedule",
      action: (report) => sendNow({ id: report.id }),
    },
    {
      label: "saved_report_history",
      icon: "pi-history",
      isAllowed: true,
      action: (report) => {
        loadHistory({ id: report.id });
        setDialog({ type: "history", report });
      },
    },
    {
      label: "delete",
      icon: "pi-trash",
      isAllowed: true,
      action: confirmDelete,
    },
  ];

  const columns = [
    { field: "name", header: "name" },
    {
      field: "level",
      header: "saved_report_level",
      body: (report) =>
        intl.formatMessage({ id: fromReportLevel(report.level) }),
    },
    {
      field: "schedule",
      header: "saved_report_schedule",
      body: (report) => describeSchedule(report.schedule, intl),
    },
    {
      field: "state",
      header: "saved_report_state",
      body: (report) => describeScheduleState(report.schedule, intl),
    },
    {
      field: "lastSend",
      header: "saved_report_last_result",
      body: (report) => (
        <div>
          <div>{describeLastSend(report.lastSend, intl)}</div>
          {(sendReason(report.lastSend) && (
            <small>{sendReason(report.lastSend)}</small>
          )) ||
            null}
        </div>
      ),
    },
    {
      field: "lastSentAtUtc",
      header: "saved_report_last_sent",
      // The latest send that was delivered; an API without the field yet
      // reads as never sent.
      body: (report) =>
        report.lastSentAtUtc
          ? formatDateTime(report.lastSentAtUtc, intl)
          : intl.formatMessage({ id: "saved_report_not_sent_yet" }),
    },
    {
      field: "actions",
      headerIcon: "pi-cog",
      align: "center",
      body: ListCellRenders.OtherActions(rowActions),
    },
  ];

  const dialogReport = dialog?.report;

  return (
    <div>
      <CustomBreadCrumbs labels={labels} navigate={navigate} />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title">
            {intl.formatMessage({ id: "saved_reports" })}
          </div>
        </div>
        <div className="edexlms-page-body">
          <div className="edexlms-search-filter-bar">
            <TextInput
              iconPosition="left"
              icon="pi-search"
              placeholder={intl.formatMessage({ id: "search_saved_reports" })}
              input={{
                name: "search",
                value: searchText,
                onChange: (e) => setSearchText(e.target.value),
              }}
            />
          </div>
          {(!requesting && !namePattern && reports.length === 0 && (
            <p>{intl.formatMessage({ id: "saved_report_none" })}</p>
          )) || (
            <List
              columns={columns}
              data={reports}
              loading={requesting && reports.length === 0}
              selectionMode={null}
              pagination={false}
              handleRowClick={(event) => openReport(event.data)}
            />
          )}
        </div>
      </div>
      <SaveReportDialog
        visible={dialog?.type === "rename"}
        title={intl.formatMessage({ id: "saved_report_rename_title" })}
        initialName={dialogReport?.name || ""}
        onHide={closeDialog}
        onSubmit={(name) => {
          // A name-only body is a rename: the API keeps the level and filters.
          updateSavedReport({ id: dialogReport.id, name });
          closeDialog();
        }}
      />
      <ScheduleDialog
        visible={dialog?.type === "schedule"}
        reportName={dialogReport?.name || ""}
        schedule={dialogReport?.schedule || null}
        onHide={closeDialog}
        onSubmit={(schedule) => {
          saveSchedule({ id: dialogReport.id, ...schedule });
          closeDialog();
        }}
        onRemove={() => {
          deleteSchedule({ id: dialogReport.id });
          closeDialog();
        }}
      />
      <SendHistoryDialog
        visible={dialog?.type === "history"}
        reportName={dialogReport?.name || ""}
        history={history}
        onHide={closeDialog}
      />
      <ConfirmDialog />
    </div>
  );
};

SavedReportList.propTypes = {
  labels: PropTypes.array,
  additionalProps: PropTypes.object,
  savedReports: PropTypes.array,
  requesting: PropTypes.bool,
  history: PropTypes.array,
  sendingNow: PropTypes.array,
  searches: PropTypes.object,
  loadSavedReports: PropTypes.func.isRequired,
  setSearch: PropTypes.func.isRequired,
  updateSavedReport: PropTypes.func.isRequired,
  deleteSavedReport: PropTypes.func.isRequired,
  saveSchedule: PropTypes.func.isRequired,
  pauseSchedule: PropTypes.func.isRequired,
  resumeSchedule: PropTypes.func.isRequired,
  deleteSchedule: PropTypes.func.isRequired,
  sendNow: PropTypes.func.isRequired,
  loadHistory: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  savedReports: state.savedReports.list,
  requesting: state.savedReports.requesting,
  history: state.savedReports.history,
  sendingNow: state.savedReports.sendingNow || [],
  searches: state.savedReports.searches || {},
});

const mapDispatchToProps = (dispatch) => ({
  loadSavedReports: (query) => dispatch(savedReportListRequest(query)),
  setSearch: (search) => dispatch(savedReportSetSearch(search)),
  updateSavedReport: (data) => dispatch(savedReportUpdateRequest(data)),
  deleteSavedReport: (data) => dispatch(savedReportDeleteRequest(data)),
  saveSchedule: (data) => dispatch(savedReportScheduleSaveRequest(data)),
  pauseSchedule: (data) => dispatch(savedReportSchedulePauseRequest(data)),
  resumeSchedule: (data) => dispatch(savedReportScheduleResumeRequest(data)),
  deleteSchedule: (data) => dispatch(savedReportScheduleDeleteRequest(data)),
  sendNow: (data) => dispatch(savedReportSendNowRequest(data)),
  loadHistory: (data) => dispatch(savedReportHistoryRequest(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SavedReportList);
